import { ConditionalFieldDefinition } from "../../../../common/models/conditionalRules/conditionalFieldDefinition";
import CharacterCountFactory from "../characterCounts/characterCountFactory";
import FormElement from "../formElements/formElement";
import { Capability } from "../formElements/formElementCapability";
import PaymentLineItemAmountFactory from "../paymentLineItems/paymentLineItemAmountFactory";
import WorkflowStepActionJumpToFactory from "../workflow/workflowStepActionJumpToFactory";

export interface FormElementConditionalField extends ConditionalFieldDefinition {
  capability?: Capability;
}

// Defines the conditional fields that reference form elements.
export default class FormElementConditionalFields {
  static DEFAULT_SHOW: FormElementConditionalField = {
    fieldName: 'default_show',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value ? 'Show' : 'Hide'
  };
  static IS_REQUIRED: FormElementConditionalField = {
    fieldName: 'is_required',
    defaultConditionalValue: false,
    friendlyName: (r) => r.conditional_value ? 'Is required' : 'Is not required'
  };
  static IS_READONLY: FormElementConditionalField = {
    fieldName: 'is_readonly',
    defaultConditionalValue: false,
    friendlyName: (r) => r.conditional_value ? 'Is readonly' : 'Is not readonly'
  };
  static LABEL_TITLE: FormElementConditionalField = {
    fieldName: 'label_title',
    defaultConditionalValue: '',
    friendlyName: (r) => r.conditional_value ? 'Title is' : 'Title is not'
  };
  static CHARACTER_COUNT: FormElementConditionalField = {
    fieldName: 'character_count',
    defaultConditionalValue: (parent, json) => CharacterCountFactory.create(parent, parent instanceof FormElement ? 'character_count' : 'conditional_value'),
    friendlyName: (r) => {
      if (r.conditional_value) {
        return `Character Length - min: ${r.conditional_value.min || 'No min'}, max: ${r.conditional_value.max || 'No max'}`
      } else {
        return 'Character length is not set: ';
      }
    },
    capability: Capability.lengthable
  };
  static JUMP_TO: FormElementConditionalField = {
    fieldName: 'jump_to',
    defaultConditionalValue: (parent, json) => WorkflowStepActionJumpToFactory.create(parent),
    friendlyName: (r) => r.conditional_value
  };
  static PAYMENT_LINE_ITEM_AMOUNT: FormElementConditionalField = {
    fieldName: 'amount',
    defaultConditionalValue: (parent, json) => PaymentLineItemAmountFactory.create(parent),
    friendlyName: (r) => r.conditional_value ? `$${r.conditional_value}` : 'Is not included'
  };

  static VALIDATION_RULE: FormElementConditionalField = {
    fieldName: 'validation_rule',
    defaultConditionalValue: true,
    friendlyName: (r) => r.conditional_value ? 'is not valid' : 'is valid'
  };

  static CONDITIONAL_PDF_GENERATION: FormElementConditionalField = {
    fieldName: 'conditional_pdf_generation',
    defaultConditionalValue: '',
    friendlyName: (r) => r.conditional_value
  };


  static FORM_ELEMENT_FIELDS: FormElementConditionalField[] = [
    FormElementConditionalFields.DEFAULT_SHOW,
    FormElementConditionalFields.IS_REQUIRED,
    FormElementConditionalFields.IS_READONLY,
    FormElementConditionalFields.LABEL_TITLE,
    FormElementConditionalFields.CHARACTER_COUNT,
    FormElementConditionalFields.VALIDATION_RULE,
  ];
  static WORKFLOW_STEP_FIELDS: FormElementConditionalField[] = [
    FormElementConditionalFields.JUMP_TO,
  ];
  static WORKFLOW_STEP_ACTION_FIELDS: FormElementConditionalField[] = [
    FormElementConditionalFields.JUMP_TO,
  ];
  static PAYMENT_LINE_ITEM_FIELDS: FormElementConditionalField[] = [
    FormElementConditionalFields.PAYMENT_LINE_ITEM_AMOUNT,
  ];

  static SDAT_PDF_FIELDS: FormElementConditionalField[] = [
    FormElementConditionalFields.CONDITIONAL_PDF_GENERATION,
  ];

  static ALL_FIELDS: FormElementConditionalField[] = Array.from(new Set([
    ...FormElementConditionalFields.FORM_ELEMENT_FIELDS,
    ...FormElementConditionalFields.WORKFLOW_STEP_FIELDS,
    ...FormElementConditionalFields.WORKFLOW_STEP_ACTION_FIELDS,
    ...FormElementConditionalFields.PAYMENT_LINE_ITEM_FIELDS,
  ]));

  static can(definition: FormElementConditionalField, control: FormElement): boolean {
    if (definition.capability === null || definition.capability === undefined) {
      return true;
    } else {
      return control.can(definition.capability);
    }
  }
}
